import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Sidebar, MobileSidebar, BottomNav } from './components/Sidebar.jsx';
import Topbar          from './components/Topbar.jsx';
import DashboardPage   from './components/DashboardPage.jsx';
import JournalPage     from './components/JournalPage.jsx';
import NotebookPage    from './components/NotebookPage.jsx';
import SettingsPage    from './components/SettingsPage.jsx';
import AccountsPage    from './components/AccountsPage.jsx';
import AnalyticsPage   from './components/AnalyticsPage.jsx';
import AiCoachPage     from './components/AiCoachPage.jsx';
import WhatIfPage      from './components/WhatIfPage.jsx';
import ChartPage       from './components/ChartPage.jsx';
import AuthPage        from './components/AuthPage.jsx';
import LandingPage     from './components/LandingPage.jsx';
import ResetPasswordPage from './components/ResetPasswordPage.jsx';
import OnboardingPage  from './components/OnboardingPage.jsx';
import TradeModal      from './components/TradeModal.jsx';
import OnboardingTour  from './components/OnboardingTour.jsx';
import ReferralPage    from './components/ReferralPage.jsx';
import ToolsPage       from './components/ToolsPage.jsx';
import IndicatorsPage  from './components/IndicatorsPage.jsx';
import PricingPage     from './components/PricingPage.jsx';
import ToastContainer  from './components/ToastContainer.jsx';
import RulesNudge      from './components/RulesNudge.jsx';
import FeedbackWidget  from './components/FeedbackWidget.jsx';
// ── ADDED: Admin portal ───────────────────────────────────────
import AdminLayout          from './components/admin/AdminLayout.jsx';
// ─────────────────────────────────────────────────────────────
import { toastSuccess, toastError, toastInfo } from './utils/toast.js';
import { setAccessToken, clearAccessToken, authApi, api, tradesApi } from './utils/api.js';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import TermsOfService from './pages/TermsOfService.jsx';
import AboutPage from './pages/AboutPage.jsx';

export default function App() {
  const [session,       setSession]       = useState(null);
  const [profile,       setProfile]       = useState(null);
  const [onboarded,     setOnboarded]     = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(() => localStorage.getItem('kfx_page') || 'd');
  const [adminPage,     setAdminPage]     = useState(() => localStorage.getItem('kfx_admin_page') || 'overview');
  const [pageData,      setPageData]      = useState(null);
  const [trades,        setTrades]        = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [legacyCount,   setLegacyCount]   = useState(0);      // unassigned trades
  const [claimingLegacy, setClaimingLegacy] = useState(false); // claim in progress
  const [editTrade,     setEditTrade]     = useState(null);
  const [draftTrade,    setDraftTrade]    = useState(null);    // persists form when modal dismissed by overlay click
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [showTour,      setShowTour]      = useState(false);
  const [resetDone,     setResetDone]     = useState(false);
  const [showLanding,   setShowLanding]   = useState(false); // never show landing on refresh — only show if bootstrap confirms no session
  const [pricingIntent, setPricingIntent] = useState(false); // true when user clicked "Notify Me" from landing → redirect to pricing after auth
  const [legalPage,     setLegalPage]     = useState(() => {
    const p = window.location.pathname;
    if (p === '/privacy') return 'privacy';
    if (p === '/terms')   return 'terms';
    if (p === '/about')   return 'about';
    return null;
  });
  // ── Notebook badge state ───────────────────────────────────────
  const [unsavedNoteDrafts, setUnsavedNoteDrafts] = useState(0);
  const [noteCount,         setNoteCount]         = useState(0);
  // ── Referral badge state ───────────────────────────────────────
  const [referralCount,     setReferralCount]     = useState(0);
  // ── Trading accounts (for sidebar switcher) ────────────────────
  const [tradingAccounts,   setTradingAccounts]   = useState([]);
  const [activeAccountId,   setActiveAccountId]   = useState(null);
  // ── Account form modal (triggered from sidebar) ────────────────
  const [showAccountForm,   setShowAccountForm]   = useState(false);

  // ── Derived: full active account object ───────────────────────
  const activeAccount = tradingAccounts.find(a => a.id === activeAccountId) ?? null;

  // Pages that require an account to be selected
  const ACCOUNT_GATED_PAGES = ['d', 'analytics', 'j', 'sim', 'ai', 'chart', 'tools'];
  const pageNeedsAccount = ACCOUNT_GATED_PAGES.includes(page);
  const noAccountSelected = pageNeedsAccount && !activeAccount;

  // ── Derived: trades strictly filtered to active account ────────
  // No active account → empty (gate enforces selection before data shows)
  const accountTrades = activeAccount
    ? trades.filter(t => t.accountId === activeAccount.id || !t.accountId)
    : [];

  // ── Fetch profile + goals from DB ─────────────────────────────
  const fetchProfile = async () => {
    try {
      const data = await api('/profile');
      if (data.profile?.level) {
        setProfile({
          name:        data.user?.name          || 'Trader',
          level:       data.profile.level,
          accountSize: String(data.goals?.accountSize || ''),
          rules:       Array.isArray(data.profile.tradingRules) ? data.profile.tradingRules : [],
        });
        // Load trading accounts for sidebar switcher + TradeModal
        const accounts  = Array.isArray(data.profile.tradingAccounts) ? data.profile.tradingAccounts : [];
        // Prefer the persisted selection from localStorage (survives refresh),
        // fall back to the server-stored activeAccountId
        const persistedId = localStorage.getItem(`kfx:active_account:${data.user?.id}`) || null;
        const activeId    = (persistedId && accounts.find(a => a.id === persistedId))
          ? persistedId
          : (data.profile.activeAccountId || null);
        if (accounts.length) {
          setTradingAccounts(accounts);
          setActiveAccountId(activeId);
          // Seed sessionStorage so TradeModal self-resolves on open
          // Key is scoped to userId so two users on same browser never share data
          try {
            sessionStorage.setItem(`kfx:accounts:${data.user?.id}`, JSON.stringify({
              tradingAccounts: accounts,
              activeAccountId: activeId,
            }));
            // Also write the current user id so TradeModal knows which key to read
            sessionStorage.setItem('kfx:current_user', data.user?.id || '');
          } catch (_) {}
          // Notify all listeners (Topbar, TradeModal, etc.)
          window.dispatchEvent(new CustomEvent('kfx:accounts-updated', {
            detail: { tradingAccounts: accounts, activeAccountId: activeId },
          }));
        }
        // Return activeId directly — callers must NOT rely on React state
        // (setActiveAccountId is async; state won't be updated yet on the next line)
        return { done: true, activeId: activeId || null };
      }
      return { done: false, activeId: null };
    } catch {
      return { done: false, activeId: null };
    }
  };

  // ── Check for unassigned legacy trades ────────────────────────
  const fetchLegacyCount = async () => {
    try {
      const count = await tradesApi.legacyCount();
      setLegacyCount(count);
    } catch {}
  };

  // ── Fetch trades for a specific account from DB ───────────────
  const fetchTrades = useCallback(async (accountId = null) => {
    setTradesLoading(true);
    try {
      const all = await tradesApi.list(accountId);
      setTrades(all);
    } catch (err) {
      console.error('[fetchTrades]', err);
    } finally {
      setTradesLoading(false);
    }
  }, []);

  // ── Fetch note count for sidebar badge ────────────────────────
  const fetchNoteCount = useCallback(async () => {
    try {
      const data = await api('/notebooks?limit=100');
      setNoteCount(typeof data.total === 'number' ? data.total : (data.notes?.length ?? 0));
    } catch {}
  }, []);

  // ── Fetch referral count for sidebar badge ─────────────────────
  const fetchReferralCount = useCallback(async () => {
    try {
      const data = await api('/referral/dashboard');
      setReferralCount(data?.stats?.totalReferrals ?? 0);
    } catch {}
  }, []);

  // ── On mount: restore session from httpOnly cookie ─────────────
  useEffect(() => {
    authApi.refresh()
      .then(async data => {
        setAccessToken(data.accessToken);
        localStorage.setItem('kfx_logged_in', '1'); // persist login hint across reloads
        const user = data.user; // /auth/refresh already returns user — no need for authApi.me()

        // Restore the page the user was on before the refresh
        const savedPage = localStorage.getItem('kfx_page') || 'd';

        flushSync(() => {
          setSession(user);
          setPage(savedPage);
          setShowLanding(false);
          setShowTour(!localStorage.getItem(`kfx_tour_done_${user.id}`));
          setLoading(false);
        });

        if (user.role === 'admin') return;

        const { done, activeId } = await fetchProfile();
        setOnboarded(done);
        if (done) {
          // Use activeId returned directly from fetchProfile — DO NOT re-read
          // localStorage here because fetchProfile already resolved the priority
          // (localStorage > server value) and React state isn't updated yet.
          fetchTrades(activeId);
          fetchLegacyCount();
          fetchNoteCount();
          fetchReferralCount();
        }
      })
      .catch((err) => {
        console.warn('[bootstrap] refresh failed:', err?.status, err?.message);
        flushSync(() => {
          setShowLanding(true);
          setLoading(false);
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Listen for forced logout broadcast from api.js ─────────────
  useEffect(() => {
    const handler = () => {
      // Clear user-scoped account cache
      try {
        const uid = sessionStorage.getItem('kfx:current_user') || '';
        localStorage.removeItem(`kfx:active_account:${uid}`);
        sessionStorage.removeItem(`kfx:accounts:${uid}`);
        sessionStorage.removeItem('kfx:current_user');
      } catch (_) {}
      clearAccessToken();
      setSession(null);
      setProfile(null);
      setTrades([]);
      setOnboarded(false);
      setShowTour(false);
      setPage('d');
      setAdminPage('overview');
      setPageData(null);
      setShowLanding(true);
      setNoteCount(0);
      setUnsavedNoteDrafts(0);
      setReferralCount(0);
      setTradingAccounts([]);
      setActiveAccountId(null);
      localStorage.removeItem('kfx_page');  // clear saved page on forced logout
      localStorage.removeItem('kfx_admin_page'); // clear saved admin tab on forced logout
      localStorage.removeItem('kfx_logged_in'); // clear session hint
      // Clean the URL so refreshing /app or /dashboard doesn't re-trigger auth issues
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        window.history.replaceState({}, '', '/');
      }
    };
    window.addEventListener('kfx:logout', handler);
    return () => window.removeEventListener('kfx:logout', handler);
  }, []);

  // ── Sync trading accounts from SettingsPage CRUD events ────────
  useEffect(() => {
    const handler = (e) => {
      if (Array.isArray(e.detail?.tradingAccounts)) {
        const newActiveId = e.detail.activeAccountId ?? null;
        setTradingAccounts(e.detail.tradingAccounts);
        setActiveAccountId(newActiveId);
        // Skip refetch when the event originated from App itself —
        // handleSetActiveAccount already calls fetchTrades(id) directly.
        if (e.detail.source !== 'app') {
          fetchTrades(newActiveId);
        }
      }
    };
    window.addEventListener('kfx:accounts-updated', handler);
    return () => window.removeEventListener('kfx:accounts-updated', handler);
  }, []);

  // ── AuthPage callback ──────────────────────────────────────────
  const handleLogin = async (user) => {
    localStorage.setItem('kfx_logged_in', '1');
    setShowLanding(false);
    setSession(user);
    setShowTour(!localStorage.getItem(`kfx_tour_done_${user.id}`));
    // Always wipe the previous user's account data before loading the new user's profile
    setTradingAccounts([]);
    setActiveAccountId(null);
    setTrades([]);

    // Consume plan intent stored in sessionStorage before the auth redirect.
    // 'free' → go to dashboard (no payment gate today).
    // Future: 'pro'/'lifetime' → route to checkout here.
    let pendingPlan = null;
    try {
      pendingPlan = sessionStorage.getItem('kfx:pending_plan');
      if (pendingPlan) sessionStorage.removeItem('kfx:pending_plan');
    } catch (_) {}

    // ADDED: admins skip onboarding and data fetching entirely
    if (user.role === 'admin') return;

    if (user.isNew) {
      // Brand new user — OnboardingPage runs, then handleOnboardingDone routes to dashboard
      setProfile(null);
      setOnboarded(false);
    } else {
      const { done, activeId } = await fetchProfile();
      setOnboarded(done);
      if (done) {
        fetchTrades(activeId);
        fetchNoteCount();
        fetchReferralCount();
        // Routing priority after login:
        // 1. Explicit "Notify Me" / pricing intent (legacy) → pricing page
        // 2. Free-plan click, direct login, or anything else → dashboard
        if (pricingIntent) {
          setPricingIntent(false);
          handleSetPage('pricing');
        } else {
          handleSetPage('d');
        }
      }
    }
  };

  // ── Handle Google OAuth redirect ───────────────────────────────
  // Must be after handleLogin is defined
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    if (!window.location.pathname.includes('/auth/google/success') || !token) return;
    window.history.replaceState({}, '', '/');
    setAccessToken(token);
    localStorage.setItem('kfx_logged_in', '1');
    authApi.me().then(async (user) => {
      await handleLogin(user);
    }).catch(() => {
      setShowLanding(true);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnboardingDone = async () => {
    const { activeId } = await fetchProfile();
    setOnboarded(true);
    fetchTrades(activeId);
    fetchNoteCount();
    fetchReferralCount();
    // After onboarding always land on dashboard.
    // pricingIntent is for users who clicked "Notify Me" — they already have
    // an account, so onboarding never runs for them. Safe to clear and ignore.
    if (pricingIntent) setPricingIntent(false);
    handleSetPage('d');
  };

  // ── Logout ─────────────────────────────────────────────────────
  const handleLogout = async () => {
    try { await authApi.logout(); } catch {}
    toastInfo('Signed out. See you next time! 👋');
    // Clear user-scoped account cache so next user on this browser starts clean
    try {
      const uid = session?.id || '';
      localStorage.removeItem(`kfx:active_account:${uid}`);
      sessionStorage.removeItem(`kfx:accounts:${uid}`);
      sessionStorage.removeItem('kfx:current_user');
    } catch (_) {}
    clearAccessToken();
    setSession(null);
    setProfile(null);
    setTrades([]);
    setTradingAccounts([]);
    setActiveAccountId(null);
    setOnboarded(false);
    setShowTour(false);
    setPage('d');
    setPageData(null);
    setShowLanding(true);
    setNoteCount(0);
    setUnsavedNoteDrafts(0);
    setReferralCount(0);
    localStorage.removeItem('kfx_page');  // clear saved page on logout
    localStorage.removeItem('kfx_admin_page'); // clear saved admin tab on logout
    localStorage.removeItem('kfx_logged_in'); // clear session hint
  };

  // ── Save trade ─────────────────────────────────────────────────
  const handleSave = (savedTrade) => {
    if (editTrade) {
      setTrades(prev => prev.map(t => t.id === savedTrade.id ? savedTrade : t));
      toastSuccess('Trade updated');
    } else {
      setTrades(prev => [savedTrade, ...prev]);
      toastSuccess('Trade logged successfully');
    }
    setShowModal(false);
    setEditTrade(null);
    setDraftTrade(null);
  };

  // ── Log trade from ChartPage ───────────────────────────────────
  const handleLogFromChart = async (data) => {
    try {
      const created = await tradesApi.create(data);
      setTrades(prev => [created, ...prev]);
      toastSuccess('Trade logged from chart');
    } catch (err) {
      console.error('[handleLogFromChart]', err);
      toastError('Failed to log trade');
    }
  };

  // ── Bulk import ────────────────────────────────────────────────
  const handleImportTrades = async (parsedTrades) => {
    try {
      await tradesApi.import(parsedTrades);
      await fetchTrades(activeAccountId);
      toastSuccess(`Imported <strong>${parsedTrades.length}</strong> trade${parsedTrades.length !== 1 ? 's' : ''} successfully`);
    } catch (err) {
      console.error('[handleImportTrades]', err);
      toastError('Import failed — please try again');
      throw err;
    }
  };

  // ── Delete trade ───────────────────────────────────────────────
  const deleteToastTimer = useRef(null);
  const deleteToastCount = useRef(0);

  const handleDelete = async (id) => {
    const snapshot = trades;
    setTrades(prev => prev.filter(t => t.id !== id));

    // Debounce: collapse rapid bulk deletes into one summary toast
    deleteToastCount.current += 1;
    clearTimeout(deleteToastTimer.current);
    deleteToastTimer.current = setTimeout(() => {
      const n = deleteToastCount.current;
      toastInfo(n === 1 ? 'Trade deleted' : `${n} trades deleted`);
      deleteToastCount.current = 0;
    }, 350);

    try {
      await tradesApi.delete(id);
    } catch (err) {
      console.error('[handleDelete]', err);
      setTrades(snapshot);
      toastError('Failed to delete trade');
    }
  };

  const openNewTrade  = ()  => {
    if (!activeAccount) {
      // Redirect to accounts page if no account selected
      handleSetPage('accounts');
      setTimeout(() => window.dispatchEvent(new CustomEvent('kfx:open-add-account')), 120);
      toastInfo('Select or create an account first to log trades.');
      return;
    }
    setEditTrade(null);
    // Don't clear draftTrade here — let the modal restore it
    setShowModal(true);
  };
  const openEditTrade = t   => { setEditTrade(t); setDraftTrade(null); setShowModal(true); };
  // Intentional close (X button or Cancel) — clears the draft
  const closeModal    = ()  => { setShowModal(false); setEditTrade(null); setDraftTrade(null); };
  // Overlay click — saves draft so user doesn't lose their work
  const dismissModal  = (currentForm) => { setShowModal(false); if (currentForm) setDraftTrade(currentForm); };

  // ── Navigate with optional payload ────────────────────────────
  const handleSetPage = useCallback((key, data = null) => {
    setPage(key);
    setPageData(data);
    localStorage.setItem('kfx_page', key);
    fetchReferralCount();
  }, [fetchReferralCount]);

  const handleProfileUpdated = async () => {
    const { activeId } = await fetchProfile();
    fetchTrades(activeId);
    toastSuccess('Profile updated');
  };

  // ── Tour ───────────────────────────────────────────────────────
  const handleTourFinish = () => {
    if (session?.id) localStorage.setItem(`kfx_tour_done_${session.id}`, '1');
    setShowTour(false);
  };

  // ── Badges ────────────────────────────────────────────────────
  const badges = { j: accountTrades.length, nb: unsavedNoteDrafts, nbCount: noteCount, referral: referralCount };

  // ── Loading spinner ────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#050402', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:36, height:36, border:'2px solid rgba(212,168,67,.2)', borderTopColor:'#D4A843', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );

  // ── Route: legal pages (accessible logged-in or not) ─────────
  const navigateLegal = (p) => {
    setLegalPage(p === 'home' ? null : p);
    window.history.pushState({}, '', p === 'home' ? '/' : `/${p}`);
    window.scrollTo(0, 0);
  };

  // ── Route: password reset link ─────────────────────────────────
  const resetToken = new URLSearchParams(window.location.search).get('token');
  if (window.location.pathname === '/reset-password' && resetToken && !resetDone) {
    return <ResetPasswordPage token={resetToken} onDone={() => {
      window.history.replaceState({}, '', '/');
      setResetDone(true);
    }} />;
  }

  // ── Route: /register?ref=KFX-XXXX → skip landing, open auth in signup mode ──
  // Guard: if already logged in, skip auth and go straight to app
  if (!session && window.location.pathname === '/register') {
    return <AuthPage onLogin={handleLogin} initialMode="signup" onNavigate={navigateLegal} />;
  }

  // ── AuthPage guard: logged-in user navigating to /login → send to app ──────
  // This handles browser back-button, restored tabs, and direct URL entry.
  if (session && (window.location.pathname === '/login' || window.location.pathname === '/register')) {
    window.history.replaceState({}, '', '/');
  }

  if (legalPage === 'privacy') return <PrivacyPolicy onNavigate={navigateLegal} />;
  if (legalPage === 'terms')   return <TermsOfService onNavigate={navigateLegal} />;
  if (legalPage === 'about')   return <AboutPage onNavigate={navigateLegal} />;

  // ── Route: not logged in ───────────────────────────────────────
  if (!session) {
    if (showLanding) return <LandingPage
      onGetStarted={() => {
        // User clicked "Start Free" on landing — store free-plan intent so
        // after they register we route them straight to the dashboard.
        try { sessionStorage.setItem('kfx:pending_plan', 'free'); } catch (_) {}
        setShowLanding(false);
      }}
      onNavigate={navigateLegal}
      onGoToPricing={() => { setPricingIntent(true); setShowLanding(false); }}
    />;
    return <AuthPage onLogin={handleLogin} onNavigate={navigateLegal} />;
  }

  // ── ADDED: Route: admin → admin portal ────────────────────────
  // Runs before onboarding check so admins never see OnboardingPage
  const handleSetAdminPage = (key) => {
    setAdminPage(key);
    localStorage.setItem('kfx_admin_page', key);
  };

  if (session.role === 'admin') {
    return <AdminLayout user={session} onLogout={handleLogout} page={adminPage} onSetPage={handleSetAdminPage} />;
  }
  // ─────────────────────────────────────────────────────────────

  // ── Route: logged in but no profile → onboarding ──────────────
  if (!onboarded) return <OnboardingPage session={session} onDone={handleOnboardingDone} />;

  // ── Sidebar account handlers ───────────────────────────────────
  const handleSetActiveAccount = (id) => {
    setActiveAccountId(id);
    // Persist selection to localStorage scoped to userId — survives page refresh
    try {
      const uid = session?.id || '';
      localStorage.setItem(`kfx:active_account:${uid}`, id);
      // Also update sessionStorage for TradeModal self-seeding
      sessionStorage.setItem(`kfx:accounts:${uid}`, JSON.stringify({
        tradingAccounts,
        activeAccountId: id,
      }));
      sessionStorage.setItem('kfx:current_user', uid);
    } catch (_) {}
    // Broadcast to all listeners: TradeModal, Topbar, DashboardPage
    // source:'app' tells the listener this came from App itself — skip refetch
    window.dispatchEvent(new CustomEvent('kfx:accounts-updated', {
      detail: { tradingAccounts, activeAccountId: id, source: 'app' },
    }));
    // Re-fetch trades scoped to this account from the server
    fetchTrades(id);
  };

  const handleSidebarAddAccount = () => {
    handleSetPage('accounts');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('kfx:open-add-account'));
    }, 120);
  };

  // ── Claim legacy trades → assign them to an account ───────────
  const handleClaimLegacy = async (accountId) => {
    setClaimingLegacy(true);
    try {
      const result = await tradesApi.claimLegacy(accountId);
      setLegacyCount(0);
      // Refetch trades so claimed ones now appear under the account
      fetchTrades(activeAccountId);
      toastSuccess(`${result.claimed} trade${result.claimed !== 1 ? 's' : ''} assigned to ${
        tradingAccounts.find(a => a.id === accountId)?.name || 'account'
      } ✓`);
    } catch (err) {
      toastError(err.message || 'Failed to assign trades');
    } finally {
      setClaimingLegacy(false);
    }
  };

  // ── Legacy claim banner ────────────────────────────────────────
  // Shows only when the user has trades with no account assigned yet.
  const LegacyClaimBanner = () => {
    const [selectedId, setSelectedId] = useState(activeAccountId || tradingAccounts[0]?.id || '');
    if (!legacyCount || !tradingAccounts.length) return null;

    const TYPE_ICON = { Live:'📈', Demo:'🧪', 'Prop Firm':'🏆', Paper:'📄' };

    return (
      <div style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, width: '100%', maxWidth: 560, padding: '0 16px',
        pointerEvents: 'none',
      }}>
        <div style={{
          background: 'var(--dark2)',
          border: '1px solid rgba(212,168,67,.35)',
          borderRadius: 'var(--radius)',
          padding: '16px 20px',
          boxShadow: '0 8px 40px rgba(0,0,0,.7)',
          pointerEvents: 'all',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
            <div style={{ fontSize:22, lineHeight:1, flexShrink:0 }}>📦</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--white)', marginBottom:3 }}>
                You have {legacyCount} unassigned trade{legacyCount !== 1 ? 's' : ''}
              </div>
              <div style={{ fontSize:11, color:'rgba(200,192,168,.5)', lineHeight:1.6 }}>
                These were logged before accounts were set up. Assign them to an account so they show up in your journal and stats.
              </div>
            </div>
            <button
              onClick={() => setLegacyCount(0)}
              style={{ background:'none', border:'none', color:'rgba(200,192,168,.3)', fontSize:18, cursor:'pointer', lineHeight:1, padding:'0 2px', flexShrink:0 }}
              title="Dismiss"
            >×</button>
          </div>

          {/* Account picker + claim */}
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              style={{ flex:1, minWidth:0, padding:'9px 12px', background:'var(--dark3)', border:'1px solid rgba(255,255,255,.1)', color:'var(--white)', fontSize:12, outline:'none', borderRadius:'var(--radius)', cursor:'pointer' }}
            >
              {tradingAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {TYPE_ICON[a.type] || '📊'} {a.name}{a.balance ? ` — $${parseFloat(a.balance).toLocaleString()}` : ''}
                </option>
              ))}
            </select>
            <button
              disabled={!selectedId || claimingLegacy}
              onClick={() => handleClaimLegacy(selectedId)}
              style={{
                padding: '9px 20px',
                background: claimingLegacy ? 'rgba(212,168,67,.3)' : 'linear-gradient(135deg,var(--gold),var(--gold-dim))',
                border: 'none', color: 'var(--black)', fontSize: 12, fontWeight: 700,
                borderRadius: 'var(--radius)', cursor: claimingLegacy ? 'default' : 'pointer',
                whiteSpace: 'nowrap', letterSpacing: '.04em',
              }}
            >
              {claimingLegacy ? 'Assigning…' : `Assign ${legacyCount} Trade${legacyCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Route: fully authenticated + onboarded → user journal ─────

  // ── No-account gate overlay ────────────────────────────────────
  const TYPE_COLOR = { Live:'var(--green)', Demo:'rgba(91,143,232,.9)', 'Prop Firm':'var(--gold)', Paper:'rgba(200,192,168,.6)' };
  const NoAccountGate = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:420 }}>
        {/* Icon */}
        <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(212,168,67,.08)', border:'1px solid rgba(212,168,67,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 24px' }}>
          🗂️
        </div>

        {tradingAccounts.length === 0 ? (
          <>
            <div style={{ fontSize:18, fontWeight:700, color:'var(--white)', marginBottom:10 }}>No accounts yet</div>
            <div style={{ fontSize:13, color:'rgba(200,192,168,.5)', lineHeight:1.7, marginBottom:28 }}>
              Create your first trading account to start journaling.<br/>
              Each account tracks its own trades, balance, and risk settings.
            </div>
            <button
              onClick={() => { handleSetPage('accounts'); setTimeout(() => window.dispatchEvent(new CustomEvent('kfx:open-add-account')), 120); }}
              style={{ padding:'12px 28px', background:'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:'var(--black)', fontSize:13, fontWeight:700, borderRadius:'var(--radius)', cursor:'pointer', letterSpacing:'.04em' }}>
              + Create Account
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize:18, fontWeight:700, color:'var(--white)', marginBottom:10 }}>Select an account to continue</div>
            <div style={{ fontSize:13, color:'rgba(200,192,168,.5)', lineHeight:1.7, marginBottom:28 }}>
              Your data is organised per account. Pick one below to view its trades, stats, and analytics.
            </div>
            {/* Account picker cards */}
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:28 }}>
              {tradingAccounts.map(acct => (
                <button key={acct.id} onClick={() => handleSetActiveAccount(acct.id)}
                  style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'var(--radius)', cursor:'pointer', textAlign:'left', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(212,168,67,.4)'; e.currentTarget.style.background='rgba(212,168,67,.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.08)'; e.currentTarget.style.background='rgba(255,255,255,.03)'; }}
                >
                  <div style={{ width:36, height:36, borderRadius:10, background:'rgba(212,168,67,.1)', border:'1px solid rgba(212,168,67,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                    {acct.type === 'Live' ? '📈' : acct.type === 'Demo' ? '🧪' : acct.type === 'Prop Firm' ? '🏆' : '📄'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--white)', marginBottom:2 }}>{acct.name}</div>
                    <div style={{ fontSize:11, color:'rgba(200,192,168,.4)' }}>
                      <span style={{ color: TYPE_COLOR[acct.type] || 'var(--gold)', fontWeight:600 }}>{acct.type}</span>
                      {acct.balance ? ` · $${parseFloat(acct.balance).toLocaleString()}` : ''}
                      {acct.broker  ? ` · ${acct.broker}` : ''}
                    </div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,67,.5)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              ))}
            </div>
            <button onClick={() => handleSetPage('accounts')}
              style={{ background:'none', border:'none', fontSize:12, color:'rgba(200,192,168,.3)', cursor:'pointer', textDecoration:'underline', textDecorationColor:'rgba(200,192,168,.15)' }}>
              Manage accounts →
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="app-root">
      <Sidebar
        page={page} setPage={handleSetPage} badges={badges}
        onLogout={handleLogout} userProfile={profile}
        tradingAccounts={tradingAccounts}
        activeAccountId={activeAccountId}
        onSetActiveAccount={handleSetActiveAccount}
        onAddAccount={handleSidebarAddAccount}
      />
      <MobileSidebar
        page={page} setPage={handleSetPage} badges={badges}
        open={mobileSidebar} onClose={() => setMobileSidebar(false)}
        onLogout={handleLogout} userProfile={profile}
        tradingAccounts={tradingAccounts}
        activeAccountId={activeAccountId}
        onSetActiveAccount={handleSetActiveAccount}
        onAddAccount={handleSidebarAddAccount}
      />

      <div className="main-col">
        <Topbar page={page} onNewTrade={openNewTrade} onHamburger={() => setMobileSidebar(v => !v)} setPage={handleSetPage} session={session} tradingAccounts={tradingAccounts} activeAccountId={activeAccountId} onSetActiveAccount={handleSetActiveAccount} />
        <div className="page-scroll" style={(page === 'chart' || page === 'tools') ? { padding:0, overflow:'hidden', height:'100%' } : {}}>

          {/* ── Pages that require an account selected ── */}
          {noAccountSelected ? <NoAccountGate /> : (
            <>
              {page === 'd'         && <DashboardPage trades={accountTrades} setPage={handleSetPage} onNewTrade={openNewTrade} onImportTrades={handleImportTrades} nextId={0} tradesLoading={tradesLoading} activeAccount={activeAccount} tradingAccounts={tradingAccounts} userInfo={profile} />}
              {page === 'analytics' && <AnalyticsPage trades={accountTrades} user={session} activeAccount={activeAccount} onUpgrade={() => handleSetPage('pricing')} />}
              {page === 'j'         && <JournalPage trades={accountTrades} onNewTrade={openNewTrade} onEdit={openEditTrade} onDelete={handleDelete} activeAccount={activeAccount} />}
              {page === 'sim'       && <WhatIfPage trades={accountTrades} user={session} activeAccount={activeAccount} tradingAccounts={tradingAccounts} onUpgrade={() => handleSetPage('pricing')} />}
              {page === 'ai'        && <AiCoachPage selectedAccountId={activeAccount?.id} onUpgrade={() => handleSetPage('pricing')} />}
              {page === 'chart'     && <ChartPage trades={accountTrades} onLogTrade={handleLogFromChart} />}
              {page === 'tools'     && <ToolsPage trades={accountTrades} profile={profile} activeAccount={activeAccount} />}
            </>
          )}

          {/* ── Account-independent pages — always render ── */}
          {page === 'nb'         && <NotebookPage prefill={pageData} onDraftsChange={setUnsavedNoteDrafts} onNoteCountChange={setNoteCount} />}
          {page === 'settings'   && <SettingsPage session={session} onProfileUpdated={handleProfileUpdated} />}
          {page === 'accounts'   && <AccountsPage setPage={handleSetPage} />}
          {page === 'referral'   && <ReferralPage onCountChange={setReferralCount} />}
          {page === 'indicators' && <IndicatorsPage trades={accountTrades} activeAccountId={activeAccountId} />}
          {page === 'pricing'    && <PricingPage session={session} setPage={handleSetPage} onGetStarted={() => handleSetPage('d')} />}

        </div>
      </div>

      {showModal && (
        <TradeModal
          initial={editTrade}
          draft={draftTrade}
          onSave={handleSave}
          onClose={closeModal}
          onDismiss={dismissModal}
          activeAccount={activeAccount}
          rules={profile?.rules || []}
        />
      )}
      {showTour && <OnboardingTour onFinish={handleTourFinish} />}

      {/* Legacy trade claim banner — shown once until all trades are assigned */}
      <LegacyClaimBanner />

      {/* Rules nudge — shows if user has no trading rules set */}
      <RulesNudge
        rules={profile?.rules || []}
        page={page}
        onGoToSettings={() => handleSetPage('settings')}
      />

      <BottomNav page={page} setPage={handleSetPage} onNewTrade={openNewTrade} />
      <ToastContainer />
      <FeedbackWidget />
    </div>
  );
}